import { ConditionsApi } from '../conditions';
import { FilesApi } from '../files';
import { MilestonesApi } from '../milestones';
import { PhoneApi } from '../phone';
import { SegmentsApi } from '../segments';
import { StudentAssistantsApi } from '../student-assistants';
import { TagsApi } from '../tags';
import { UsersApi } from '../users';

export interface AttachmentUrl {
  url: string;
}

export enum ConversationType {
  Group = 'group',
  Internal = 'internal'
}

export enum ConversationStatus {
  Open = 'open',
  Closed = 'closed'
}

export enum DeliveryMethod {
  Email = 'email',
  Sms = 'sms',
  Web = 'web',
  PhoneCall = 'phone_call',
  WhatsApp = 'whatsapp'
}

export enum ComplianceCategory {
  Phishing = 'phishing',
  Shaft = 'shaft'
}

export interface ComplianceCheck {
  reason: string;
  compliance_categories: ComplianceCategory[];
}

export const WHATSAPP_CHARACTER_LIMIT = 4096;

export const SMS_CHARACTER_LIMIT = 160;

export type ChatbotChannels =
  | DeliveryMethod.Email
  | DeliveryMethod.Sms
  | DeliveryMethod.WhatsApp
  | DeliveryMethod.Web;

export enum DateFilter {
  Today = 'today',
  Yesterday = 'yesterday',
  ThisWeek = 'this_week',
  Last7Days = 'last_7_days',
  ThisMonth = 'this_month',
  Last30Days = 'last_30_days'
}

export enum QueryParams {
  AssignedTo = 'assigned_to',
  AssignedToOthers = 'assigned_to_others',
  IsParticipant = 'is_participant',
  IsOpened = 'is_opened',
  IsFavorite = 'is_favorite',
  HasAttachments = 'has_attachments',
  HasPhoneCallRecording = 'has_phone_call_recording',
  ExternalParticipantId = 'external_participant_id',
  SearchText = 'search_text',
  DateFilter = 'date_filter',
  TagId = 'tag_id',
  Channels = 'channels',
  SelectedConversation = 'selected_conversation',
  ChatbotAnswered = 'chatbot_answered',
  ChatbotHandoff = 'chatbot_handoff'
}

export interface SavedReplyDto {
  guid: string;
  title: string;
  subject: string;
  message: string;
  subdom: string;
  channels: DeliveryMethod[] | null | undefined;
  updated_by: string;
  updated_at: string;
  created_by: string;
  created_at: string;
}

export type SavedReplyEditableProperties = Pick<
  SavedReplyDto,
  'title' | 'subject' | 'message' | 'channels'
>;

export type CreateSavedReplyDto = SavedReplyEditableProperties;
export type UpdateSavedReplyDto = SavedReplyEditableProperties;

export interface SavedRepliesResponse {
  count: number;
  replies: SavedReplyDto[];
}

export enum SavedReplyTokens {
  SenderFirstName = '[sender:first-name]',
  SenderLastName = '[sender:last-name]',
  UserFirstName = '[user:first-name]',
  UserLastName = '[user:last-name]',
  UserPreferredName = '[user:preferred-name]',
  SchoolName = '[client:name]'
}

export const SavedReplyTokensLabels = [
  {
    value: SavedReplyTokens.UserFirstName,
    label: 'User First Name',
    group: 'External Participant'
  },
  {
    value: SavedReplyTokens.UserLastName,
    label: 'User Last Name',
    group: 'External Participant'
  },
  {
    value: SavedReplyTokens.UserPreferredName,
    label: 'User Preferred Name',
    group: 'External Participant'
  },
  {
    value: SavedReplyTokens.SenderFirstName,
    label: 'Sender First Name',
    group: 'Sender'
  },
  {
    value: SavedReplyTokens.SenderLastName,
    label: 'Sender Last Name',
    group: 'Sender'
  },
  {
    value: SavedReplyTokens.SchoolName,
    label: 'School Name',
    group: 'School'
  }
];

/**
 * Rules
 */
export type RuleOperator = 'and' | 'or';

export interface Rule {
  guid: string;
  name: string;
  description: string;
  active: boolean;
  action?: Action;
  actions?: Action[];
  priority: number;
  rule_operator: RuleOperator;
  rules: ConditionsApi.Condition[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ChatbotHandoffIntent {
  intent: string;
}

export interface ModerationFlaggedCondition {
  moderation_flags?: ModerationCategoriesEnum[];
}

export interface ChatbotHandoffRule {
  intent: string;
  guid: string;
}

export enum ChatbotHandoffType {
  Specific = 'specific',
  All = 'all'
}

export enum ChatbotTools {
  StartApplication = 'start_application',
  GetApplicationStatus = 'get_application_status',
  ApplicationStatusChecklist = 'application_status_checklist',
  ScheduleAppointment = 'schedule_appointment',
  RegisterForEvent = 'register_for_event',
  ClientKnowledgeBase = 'client_knowledge_base',
  CommonAppKnowledgeBase = 'common_app_knowledge_base',
  FinancialAidKnowledgeBase = 'financial_aid_knowledge_base',
  InquiryFlow = 'inquiry_flow',
  ProgramExplorer = 'program_explorer',
  CourseCatalog = 'course_catalog',
  TeamMember = 'team_member',
  UserDataAccess = 'user_data_access'
}

export interface ChatbotToolListItem {
  name: string;
  value: ChatbotTools;
}

export const CHATBOT_TOOLS_LIST: ChatbotToolListItem[] = [
  {
    name: 'Human Team Member Handoff',
    value: ChatbotTools.TeamMember
  },
  {
    name: 'Start an Application',
    value: ChatbotTools.StartApplication
  },
  {
    name: 'Get Application Status',
    value: ChatbotTools.GetApplicationStatus
  },
  {
    name: 'Application Status Checklist',
    value: ChatbotTools.ApplicationStatusChecklist
  },
  {
    name: 'Register for Event',
    value: ChatbotTools.RegisterForEvent
  },
  {
    name: 'Schedule Appointments',
    value: ChatbotTools.ScheduleAppointment
  },
  {
    name: 'Common App Knowledge',
    value: ChatbotTools.CommonAppKnowledgeBase
  },
  {
    name: 'Financial Aid Knowledge',
    value: ChatbotTools.FinancialAidKnowledgeBase
  },
  {
    name: 'Inquiry Flow',
    value: ChatbotTools.InquiryFlow
  },
  {
    name: 'Program Explorer',
    value: ChatbotTools.ProgramExplorer
  },
  {
    name: 'Course Catalog',
    value: ChatbotTools.CourseCatalog
  },
  {
    name: 'User Data Access',
    value: ChatbotTools.UserDataAccess
  }
];

export const ALL_CHATBOT_TOOLS = Object.values(ChatbotTools);

export enum KnowledgeBaseCategories {
  CommonApp = 'common-app',
  CourseInformation = 'course-information',
  ProgramInformation = 'program-information',
  ProgramCareerInformation = 'program-career-information',
  ProgramTermInformation = 'program-term-information',
  /**
   * Available for customers to pick
   */
  FinancialAid = 'financial-aid',
  Admissions = 'admissions',
  Academics = 'academics',
  StudentLife = 'student-life',
  Athletics = 'athletics',
  Career = 'career',
  StudentServices = 'student-services',
  General = 'general',
  Alumni = 'alumni',
  InternationStudents = 'international-students',
  DomesticStudents = 'domestic-students',
  GraduateStudents = 'graduate-students',
  UndergraduateStudents = 'undergraduate-students',
  ContinuingEducation = 'continuing-education',
  Housing = 'housing',
  TechnicalIssues = 'technical-issues',
  BusinessOffice = 'business-office',
  StudentHealthAndWellness = 'student-health-and-wellness',
  CampusSafetyAndSecurity = 'campus-safety-and-security',
  LibraryAndAcademicResources = 'library-and-academic-resources',
  DiningAndFoodServices = 'dining-and-food-services',
  ParkingAndTransportation = 'parking-and-transportation',
  Transfer = 'transfer',
  Online = 'online',
  DualCredit = 'dual-credit',
  HighSchool = 'high-school'
}

export interface Draft {
  message?: string;
  note?: string;
  conversation_guid: string;
  author_id: string;
}

export interface TypingActivity {
  conversation_guid: string;
  user_ids: string[];
  external_user_typing?: boolean;
  chatbot_typing?: boolean;
  chatbot_status?: string;
}

export interface TypingActivityItem {
  conversationGuid: string;
  isTyping: boolean;
}

export type RuleApiRequiredProps = Pick<
  Rule,
  'name' | 'action' | 'rules' | 'action'
>;
export type RuleEditableProps = Omit<
  Rule,
  'guid' | 'created_by' | 'created_at' | 'updated_at'
>;
export type RuleUpdatePayload = Partial<RuleEditableProps> &
  RuleApiRequiredProps;

export interface Action {
  type: ActionType;
  parameters: ActionParameters;
}

export enum ActionType {
  AssignTo = 'assign_to',
  ReplyBack = 'reply_back',
  TagConversation = 'tag_conversation',
  AddParticipant = 'add_participants',
  CloseConversation = 'close_conversation',
  WorkflowRule = 'execute_workflow'
}

export type ActionParameters =
  | AssignActionParameters
  | ParticipantsActionParameters
  | ReplyActionParameters
  | TagActionParameters
  | WorkflowRuleActionParameters;

export interface AssignActionParameters {
  user_id: string;
}

export enum MessengerLocationType {
  Portal = 'portal',
  App = 'app',
  Page = 'page',
  Event = 'event'
}

export interface ConversationLocation {
  host: string;
  path: string;
  type?: MessengerLocationType | null;
}

export interface ExternalConversationCreateDto {
  message?: string;
  attachment?: File | null;
  location: ConversationLocation;
  namespace?: string;
  assistant?: string;
  search_thread_guid?: string;
  test?: boolean;
  is_audio?: boolean | null;
  assignee?: string | null;
  is_private?: boolean | null;
}

export interface ExternalVoiceCallConversationCreateDto {
  location: ConversationLocation;
  namespace?: string;
  assistant?: string;
  test?: boolean;
}

export interface ConversationCreateBaseDto {
  delivery_method: DeliveryMethod;
  recipient_id: string;
  message?: string;
  inbox_setting_id?: string;
  attachment?: File;
  title?: string;
  participants?: string[];
  assignee?: string | null;
  is_private?: boolean;
  scheduled_at?: Date;
}

export interface EmailConversationCreateDto extends ConversationCreateBaseDto {
  delivery_method: DeliveryMethod.Email;
  /**
   * what email to use as destination
   */
  user_email?: string;
}

export interface SmsConversationCreateDto extends ConversationCreateBaseDto {
  delivery_method: DeliveryMethod.Sms;
  client_phone_number?: string;
  user_phone_number?: string;
}

export interface WhatsAppConversationCreateDto
  extends ConversationCreateBaseDto {
  delivery_method: DeliveryMethod.WhatsApp;
  client_phone_number?: string;
  user_phone_number?: string;
}

export interface PhoneCallConversationCreateDto
  extends ConversationCreateBaseDto {
  delivery_method: DeliveryMethod.PhoneCall;
  to_phone_number?: string;
  from_phone_number?: string;
  is_recording_on?: boolean;
  direction: 'inbound' | 'outbound';

  /**
   * only available for inbound calls during creation
   */
  phone_call_id?: string;

  ai_voice_assistant_guid?: string | 'default';
  ai_voice_assistant_goal?: string;
  transfer_to_phone_number?: string;
}

export type ConversationCreateDTO =
  | EmailConversationCreateDto
  | SmsConversationCreateDto
  | WhatsAppConversationCreateDto
  | PhoneCallConversationCreateDto;

export interface ParticipantsActionParameters {
  user_ids: string[];
}

export interface TagActionParameters {
  taxonomy_guids: string[];
}

export interface WorkflowRuleActionParameters {
  workflow_guid: string;
}

export interface ReplyActionParameters {
  message: string | File;
}

export class ConversationError extends Error {
  constructor(
    readonly statusCode: number,
    readonly errorMessage: string,
    readonly explanation?: string
  ) {
    super(errorMessage);
  }

  withExplanation(explanation: string): ConversationError {
    return new ConversationError(
      this.statusCode,
      this.errorMessage,
      explanation
    );
  }
}

export const ConversationErrors = {
  badRequest: new ConversationError(400, 'Bad request'),
  unauthorized: new ConversationError(401, 'Unauthorized'),
  tokenExpired: new ConversationError(401, 'Token expired'),
  forbidden: new ConversationError(403, 'Forbidden'),
  notFound: new ConversationError(404, 'Not Found'),
  conflict: new ConversationError(409, 'Conflict on update'),
  internal: new ConversationError(500, 'Internal server error'),
  userNotFound: new ConversationError(404, 'User not found'),
  conversationNotFound: new ConversationError(404, 'Conversation not found'),
  taxonomyNotFound: new ConversationError(404, 'Taxonomy not found'),
  partNotFound: new ConversationError(404, 'Conversation part not found'),
  fileNotFound: new ConversationError(404, 'File not found'),
  savedReplyNotFound: new ConversationError(404, 'Saved reply not found'),
  userAlreadyParticipant: new ConversationError(400, 'User already participant')
};

export enum SocketEvents {
  /**
   * Socket Response Events
   */
  ConversationCreated = 'conversation_created',
  ConversationClosed = 'conversation_closed',
  NoteAdded = 'note_added',
  NoteEdited = 'note_edited',
  CommentAdded = 'comment_added',
  CommentEdited = 'comment_edited',
  PartDeleted = 'part_deleted',
  AssigneeAdded = 'assignee_added',
  AssigneeRemoved = 'assignee_removed',
  ParticipantAdded = 'participant_added',
  ParticipantLeft = 'participant_left',
  ConversationTagged = 'conversation_tagged',
  ConversationUnTagged = 'conversation_untagged',
  ConversationRenamed = 'conversation_renamed',
  InfoCapturePrompted = 'info_capture_prompted',
  EmailCaptureResponded = 'email_capture_responded',
  NameCaptureResponded = 'name_capture_responded',
  ExternalLastVisitedUpdated = 'external_last_visited_updated',
  ConversationStudentAssistantChanged = 'conversation_student_assistant_changed',
  ConversationPrivacyChanged = 'conversation_privacy_changed',
  PartFailedToSend = 'part_failed_to_send',
  ComplianceFlagged = 'compliance_flagged',
  PhoneCallTransferred = 'phone_call_transferred',

  ConversationInboxChanged = 'conversation_inbox_changed',
  PhoneCallPartEdited = 'phone_call_part_edited',
  PhoneCallStarted = 'phone_call_started',
  ChatBotToggled = 'chatbot_toggled',
  ConversationStudentAssistantAdded = 'conversation_student_assistant_added',
  PartSentimentAnalysed = 'part_sentiment_analysed',
  MessageFlagged = 'message_flagged',
  ConversationFlagged = 'conversation_flagged',
  ConversationBlocked = 'conversation_blocked',
  WebsiteArticleSitemapLoaded = 'website_article_sitemap_loaded',
  /** This Event will be only sent to currently logged in user */
  LastVisitedUpdated = 'last_visited_updated',
  UserAssociated = 'user_associated',
  ConversationOpened = 'conversation_opened',
  AttachmentAdded = 'attachment_added',
  ConversationDeleted = 'conversation_deleted',
  UserOnline = 'user_online',
  UserOffline = 'user_offline',
  AnonymousUserUpdated = 'anonymous_user_updated',
  UsersMerged = 'users_merged',
  ExternalTypingActivityUpdated = 'external_typing_activity_updated',
  InternalTypingActivityUpdated = 'internal_typing_activity_updated',
  ChatbotTypingActivityUpdated = 'chatbot_typing_activity_updated',
  ChatbotNewMessageChunk = 'chatbot_new_message_chunk',
  UrlArticleIndexed = 'url_article_indexed',
  ArticleFromFileIndexed = 'file_article_indexed',
  YoutubeVideoArticleIndexed = 'youtube_video_article_indexed',
  PushNotificationAdded = 'push_notification_added',
  PushNotificationRead = 'push_notification_read',
  /**
   * Socket Request Events
   */
  AddComment = 'add_comment',
  AddComments = 'add_comments',
  MarkAsSeen = 'mark_as_seen',
  ErrorHappened = 'error_happened',
  UpdateExternalLastVisited = 'update_external_last_visited',
  EmailCapture = 'email_capture',
  NameCapture = 'name_capture',
  ChangeConversationStudentAssistant = 'change_conversation_student_assistant',
  UpdateExternalTypingActivity = 'update_external_typing_activity',
  UpdateInternalTypingActivity = 'update_internal_typing_activity',
  UpdateChatbotTypingActivity = 'update_chatbot_typing_activity'
}

/**
 * All socket events that should be carefully decided to whom they should be sent
 */
export const PrivateEvents = new Set([
  SocketEvents.ConversationCreated,
  SocketEvents.ConversationClosed,
  SocketEvents.ConversationTagged,
  SocketEvents.ConversationUnTagged,
  SocketEvents.ConversationOpened,
  SocketEvents.ConversationRenamed,
  SocketEvents.InfoCapturePrompted,
  SocketEvents.EmailCaptureResponded,
  SocketEvents.NameCaptureResponded,
  SocketEvents.ConversationStudentAssistantChanged,
  SocketEvents.ConversationStudentAssistantAdded,
  SocketEvents.ConversationPrivacyChanged,
  SocketEvents.NoteAdded,
  SocketEvents.NoteEdited,
  SocketEvents.CommentAdded,
  SocketEvents.CommentEdited,
  SocketEvents.PartDeleted,
  SocketEvents.AssigneeAdded,
  SocketEvents.AssigneeRemoved,
  SocketEvents.ParticipantAdded,
  SocketEvents.ParticipantLeft,
  SocketEvents.PhoneCallPartEdited,
  SocketEvents.PhoneCallStarted,
  SocketEvents.PhoneCallTransferred,
  SocketEvents.ChatBotToggled,
  SocketEvents.PartSentimentAnalysed,
  SocketEvents.ConversationInboxChanged,
  SocketEvents.ConversationDeleted,
  SocketEvents.AttachmentAdded,
  SocketEvents.MessageFlagged,
  SocketEvents.ConversationBlocked,
  SocketEvents.ConversationFlagged,
  SocketEvents.ComplianceFlagged
]);

export enum ConversationPartType {
  Comment = 'comment',
  ScheduledComment = 'scheduled_comment',
  Open = 'open',
  Close = 'close',
  Assign = 'assign',
  Unassign = 'unassign',
  AddParticipant = 'add_participant',
  RemoveParticipant = 'remove_participant',
  Rename = 'rename',
  Leave = 'leave',
  Join = 'join',
  Note = 'note',
  Attachment = 'attachment',
  EmailCapturePrompt = 'email_capture_prompt', // to keep the old parts working
  InfoCapturePrompt = 'info_capture_prompt',
  EmailCaptureResponse = 'email_capture_response',
  NameCaptureResponse = 'name_capture_response',
  PhoneCall = 'phone_call',
  ScheduledPhoneCall = 'scheduled_phone_call',
  ChatBotToggle = 'chatbot_toggle',
  AddAssistantToConversation = 'add_assistant_to_conversation',
  StudentAssistantChanged = 'student_assistant_changed',
  PrivacyChanged = 'privacy_changed',
  ConversationFlagged = 'conversation_flagged',
  ModerationBlocked = 'moderation_blocked',
  TranscriptChunk = 'transcript_chunk',
  TranscriptSummary = 'transcript_summary',
  PhoneCallTransferred = 'phone_call_transferred'
}

// this is just copied from luminous-realtime-api
export interface UserDto {
  id: string; // ObjectID hex
  first_name: string;
  last_name: string;
  email?: string;
  type: UsersApi.UserType; // external by default
  avatar?: UsersApi.UserAvatar;
  phones?: PhoneApi.Phone[];
  milestones?: MilestonesApi.Milestone[];
}

export enum ConversationPartVisibility {
  External = 'external',
  Internal = 'internal'
}

export enum ParticipantStatus {
  Seen = 'seen',
  Sent = 'sent',
  Notified = 'notified'
}

export enum ConversationPhoneCallPartState {
  PhoneCallScheduled = 'phone_call_scheduled',
  PhoneCallStarted = 'phone_call_started',
  PhoneCallCompleted = 'phone_call_completed'
}

export enum ConversationPromptPartState {
  PromptMain = 'prompt_main',
  PromptFollowUp = 'prompt_follow_up',
  PromptThankYou = 'prompt_thank_you'
}

export type ConversationPartState =
  | ConversationPhoneCallPartState
  | ConversationPromptPartState;

export interface ParticipantStatusesOfPart {
  type: UsersApi.UserType;
  status: ParticipantStatus;
  user_id?: string;
  anonymous_guid?: string;
  is_anonymous: boolean;
}

export enum PartAuthorType {
  Internal = 'internal',
  External = 'external',
  System = 'system',
  Bot = 'bot'
}

export interface PartAuthor {
  user_id?: string;
  // maybe this property is not even needed, because if it is external participant,
  // we can just check `conversation.external_participant` for author data
  anonymous_guid?: string;
  type: PartAuthorType;
  user?: UserDto;
  rule_name?: string;
}

export interface ConversationPartsResponse {
  parts: ConversationPartBaseDto[];
  hasPrevious: boolean;
}

export enum ConversationPartRating {
  Like = 'like',
  Dislike = 'dislike'
}

export interface RatePartDto {
  rating: ConversationPartRating;
}

export interface ReschedulePartDto {
  scheduled_at: string;
}

export interface ConversationPartBaseDto {
  _id: string;
  guid: string;
  subdom: string;
  conversation_guid: string;
  author: PartAuthor;
  type: ConversationPartType;
  visibility: ConversationPartVisibility;
  updated_at: string;
  created_at: string;
  // this is used for faster showing of messages on FE, even if they are still not received through socket
  temp_guid?: string;
  // conversation context
  conversation?: ConversationDto;
  content: any;
  source?: string;
  phone_call?: PhoneCallData;
  sentiment_evaluation?: number;
  rating?: ConversationPartRating;
  chatbot_handoff?: boolean;
  chatbot_sources?: ChatbotSource[];
  assistant?: ChatbotPartAssistant;
  /**
   * @deprecated
   */
  metadata?: ChatbotResponseMetadata;
  metadata_stack?: ChatbotMetadataStack;
  part_status?: {
    status?: ConversationPartStatus;
  };
  moderation_flags?: ModerationCategoriesEnum[];
  moderation_reasoning?: string;
  compliance_check?: ComplianceCheck;
  scheduled_at?: string | null;
}

export enum ConversationPartStatus {
  Sent = 'sent',
  Failed = 'failed'
}

export interface ChatbotPartAssistant {
  name: string;
  title?: string;
  guid: string;
  logo: string;
  assistant_type: StudentAssistantsApi.StudentAssistantType;
}

export interface PhoneCallData {
  to_phone_number?: string;
  from_phone_number?: string;
  is_recording_on?: boolean;
  phone_call_id?: string;
  direction?: 'inbound' | 'outbound';
  ai_voice_assistant_goal?: string;
  ai_voice_assistant_guid?: string;
  transfer_to_phone_number?: string;
}

export interface AddParticipantPartDto extends ConversationPartBaseDto {
  content: {
    target: string; // what is happening if we add multiple users at once? is it possible?
  };
}

export interface AssignPartDto extends ConversationPartBaseDto {
  content: {
    target: string;
  };
}

export interface AttachmentPartFile {
  file_id: string;
  description: string;
  size: number;
  name: string;
  mime: string;
  privacy: string;
  url: string;
  is_audio?: boolean;
  transcription?: string;
  translation?: string;
}

export interface AttachmentPartDto extends ConversationPartBaseDto {
  content: AttachmentPartFile;
}

export interface ClosePartDto extends ConversationPartBaseDto {
  content: Record<string, never>;
}

export interface CommentPartDto extends ConversationPartBaseDto {
  content: {
    message: string;
    translation?: string;
  };
}

export interface JoinPartDto extends ConversationPartBaseDto {
  content: {
    target: string;
  };
}

export interface PhoneCallTransferredPartDto extends ConversationPartBaseDto {
  content: {
    phone_call_transfer_number: string;
  };
}

export interface LeavePartDto extends ConversationPartBaseDto {
  content: {
    target: string;
  };
}

export interface NotePartDto extends ConversationPartBaseDto {
  content: {
    message: string;
  };
}

export interface OpenPartDto extends ConversationPartBaseDto {
  content: Record<string, never>;
}

export interface RemoveParticipantPartDto extends ConversationPartBaseDto {
  content: {
    target: string; // here is the same question, do we allow multiple users to be removed in one API call
  };
}

export interface RenamePartDto extends ConversationPartBaseDto {
  content: {
    old: string | null;
    new: string;
  };
}

export interface UnassignPartDto extends ConversationPartBaseDto {
  content: {
    target: string;
  };
}

export interface ChatBotTogglePartDto extends ConversationPartBaseDto {
  content: {
    chatbot_state: ChatbotState;
  };
}

export interface AddAssistantToConversationPartDto
  extends ConversationPartBaseDto {
  content: {
    student_assistant: string;
    chatbot_state: ChatbotState;
  };
}

export interface StudentAssistantChangedPartDto
  extends ConversationPartBaseDto {
  content: {
    student_assistant: string;
  };
}

export interface PrivacyChangedPartDto extends ConversationPartBaseDto {
  conversation: ConversationDto;
  content: {
    is_private: boolean;
  };
}

export interface ModerationBlockChangedPartDto extends ConversationPartBaseDto {
  conversation: ConversationDto;
  content: {
    moderation_block: boolean;
  };
}

export interface InfoCapturePromptPartDto extends ConversationPartBaseDto {
  content: {
    message: string;
    state: string;
  };
}

export interface EmailCaptureResponsePartDto extends ConversationPartBaseDto {
  content: {
    email: string;
  };
}

export interface NameCaptureResponsePartDto extends ConversationPartBaseDto {
  content: {
    first_name: string;
    last_name: string;
  };
}

export interface ConversationFlaggedPartDto extends ConversationPartBaseDto {
  content: {
    message?: string;
    moderation_flags: ModerationCategoriesEnum[];
  };
}

export interface ChatbotNewMessageChunkDto {
  conversation_guid: string;
  sequence: number;
  token: string;
  temp_guid: string;
  assistant: ChatbotPartAssistant;
}

export interface PhoneCallSummary {
  summarization?: string;
  discussed_topics?: string[];
  user_sentiment?: string;
}

export enum PhoneCallEndedReason {
  HumanTransfer = 'human_transfer',
  Silence = 'silence',
  AIError = 'ai_error'
}

export interface PhoneCallPartDto extends ConversationPartBaseDto {
  content: {
    state: ConversationPhoneCallPartState;
    call_duration?: number;
    transcription_file_guid?: string;
    recording_file_guid?: string;
    call_ended_at?: string;
    call_ended_reason?: PhoneCallEndedReason;
    phone_call_summary?: PhoneCallSummary;
  };
}

export type Part =
  | AddParticipantPartDto
  | AssignPartDto
  | AttachmentPartDto
  | ClosePartDto
  | CommentPartDto
  | JoinPartDto
  | LeavePartDto
  | NotePartDto
  | OpenPartDto
  | RemoveParticipantPartDto
  | RenamePartDto
  | UnassignPartDto
  | ChatBotTogglePartDto
  | StudentAssistantChangedPartDto
  | PrivacyChangedPartDto
  | ModerationBlockChangedPartDto
  | InfoCapturePromptPartDto
  | EmailCaptureResponsePartDto
  | NameCaptureResponsePartDto
  | ConversationFlaggedPartDto
  | PhoneCallPartDto
  | PhoneCallTransferredPartDto;

export interface AnonymousParticipantData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  is_captured?: boolean;
}

export interface AnonymousParticipantUpdatedDto {
  anonymous_guid: string;
  data: AnonymousParticipantData;
  conversation_guids: string[];
}

export interface CaptureEmailDto {
  email: string;
}

export interface CaptureNameDto {
  first_name?: string;
  last_name?: string;
}

export interface UpdateTypingActivityDto {
  is_typing: boolean;
  conversation_guid: string;
}

export interface UpdateInternalTypingActivityDto {
  is_typing: boolean;
  conversation_guid: string;
  user_id: string;
}

export interface ConversationChangeStudentAssistantDto {
  conversation_guid: string;
  student_assistant_guid: string | null;
}

export enum ChatbotTypingActivityStatus {
  Thinking = 'Thinking...',
  OneMoment = 'One Moment...',
  WorkingOnIt = 'Working on it...',
  StillThinking = 'Still Thinking...',
  AlmostThere = 'Almost There...',
  ThankYou = 'Thank you for your patience...'
}

export interface UpdateChatbotTypingActivityDto {
  conversation_guid: string;
  status: string | null;
  is_typing: boolean;
}

export interface ExternalParticipantDto {
  is_anonymous: boolean;
  user_id?: string;
  anonymous_guid?: string;
  user?: UserDto; // populated data about user
  // this is the data that we collect until we recognize user
  anonymous_data?: AnonymousParticipantData;
  is_online: boolean;
  /**
   * ISO date
   */
  last_visited: string;
  /**
   * ISO date
   */
  last_message_at: string;
}

export interface ConversationDto {
  _id: string;
  guid: string;
  subdom: string;
  author: {
    user_id?: string;
    type: PartAuthorType;
  };
  delivery_method: DeliveryMethod;
  is_private?: boolean;
  inbox_setting_id?: string;
  inbound_channel_id?: string;
  source: DeliveryMethod;
  status: ConversationStatus;
  assignee?: string | null; // I guess it is ok to be a string, because it should be an admin, that will be pre-fetched
  title?: string; // should this be only visible to internal users?
  participants: ConversationParticipantDto[];
  from?: string; // #TODO: Add support for this on API. It could be an email or phone number depending on selected deliveryMethod
  tags: string[];
  favs: string[];
  last_activity: string;
  updated_at: string;
  created_at: string;
  chatbot_answered_at?: string;
  external_participant?: ExternalParticipantDto;
  latest_part?: CommentPartDto | AttachmentPartDto | PhoneCallPartDto;
  origin_event?: any;
  location?: ConversationLocation;
  chatbot_state?: ChatbotState;
  namespace?: string;
  sms_inbox?: {
    client_phone_number: string;
    user_phone_number: string;
  };
  email_inbox?: {
    client_email?: string;
    user_email: string;
  };
  phone_call?: PhoneCallData;
  assistants?: {
    active_assistant?: string;
  };
  visibility_groups?: string[];
  moderation_flags?: ModerationCategoriesEnum[];
  moderation_block?: boolean;
}

export interface ConversationFilter {
  subdom?: string;
  is_favorite?: boolean;
  assignee?: string;
  status?: string;
  limit?: number;
}

export interface ConversationParticipantDto {
  user_id: string;
  last_visited: string; // iso date
}

export interface ConversationList {
  conversations: ConversationDto[];
  hasPrevious: boolean;
}

export interface CreateConversationResponse {
  conversation: ConversationDto;
  part: CommentPartDto;
}

export interface MessageFlaggedDto {
  conversation: ConversationDto;
  part: CommentPartDto;
}

export interface ConversationFlaggedDto {
  conversation: ConversationDto;
  part: CommentPartDto;
}

export interface ConversationBlockedDto {
  conversation: ConversationDto;
}

export interface AddSocketComment {
  message: string;
  temp_guid: string;
  conversation_guid: string;
  source?: string;
  scheduled_at?: string;
}

export interface AddSocketComments {
  conversation_guid: string;
  comments: AddSocketComment[];
}

export interface MarkAsSeenDto {
  conversation_guid: string;
  /**
   * ISO string
   */
  date: string;
}
export interface LastVisitedUpdatedDto {
  conversation_guid: string;
  /**
   * ISO date
   */
  last_visited: string;
}

export interface TokenUser {
  isAnonymous: boolean;
  anonymousGuid?: string;
  userId?: string;
  isInternal: boolean;
  type: UsersApi.UserType;
  subdom: string;
}

export interface UserOnlineEvent {
  anonymousGuid: string;
  exp: number;
  iat: number;
  isAnonymous: boolean;
  isInternal: boolean;
  subdom: string;
  type: UsersApi.UserType;
  userId: string;
}

export interface ListConversationsQuery {
  limit?: number;
  before_date?: string; // iso date
  selected_conversation?: string;
}

export interface ConversationFilterQuery extends ListConversationsQuery {
  assigned_to?: string[]; // userIds
  assigned_to_others?: boolean;
  is_participant?: boolean;
  is_anonymous?: boolean;
  external_participant_id?: string;
  channels?: DeliveryMethod[];
  is_opened?: boolean;
  is_favorite?: boolean;
  has_attachments?: boolean;
  has_phone_call_recording?: boolean;
  date_from?: string;
  date_to?: string;
  search_text?: string; // TODO: search parts content
  date_filter?: DateFilter;
  tag_id?: string[];
  chatbot_answered?: boolean;
  chatbot_handoff?: boolean;
  preview_uuid?: string;
  no_reply?: boolean;
  privacy?: boolean;
  moderation_flagged?: boolean;
  moderation_blocked?: boolean;
  scheduled?: boolean;
}

export type ConversationViewRequiredProps = Pick<
  ConversationView,
  'name' | 'filter'
>;
export type ConversationViewEditableProps = Omit<
  ConversationView,
  'guid' | 'subdom' | 'created_at' | 'updated_at'
>;
export type ConversationViewDTO = Partial<ConversationViewEditableProps> &
  ConversationViewRequiredProps;

export interface ConversationView {
  guid: string;
  subdom?: string;
  name: string;
  description?: string;
  restrict_to?: string[];
  filter: ConversationFilterQuery;
  created_at?: string;
  updated_at?: string;
}

export enum SystemViews {
  BoltAssistantHandoff = 'bolt_assistant_handoff',
  BoltAssistantParticipated = 'bolt_assistant_participated',
  PhoneCalls = 'phone_calls',
  AssignedToMe = 'assigned_to_me',
  AssignedToMeAndOpen = 'assigned_to_me_and_open',
  AssignedToMyTeam = 'assigned_to_my_team',
  AssignedToMyTeamAndOpen = 'assigned_to_my_team_and_open',
  Participating = 'participating',
  Open = 'open',
  Closed = 'closed',
  Favorite = 'favorite',
  ThisWeek = 'this_week',
  NoReply = 'no_reply'
}

export function isSystemView(view: string): boolean {
  return Object.values(SystemViews).includes(view as SystemViews);
}

export interface MessengerClient {
  pageContext?: {
    width: number;
    height: number;
    url: string;
  };
  apiUrlRT: string;
  featureToken: string;
  analyticsToken: string;
}

export interface MessengerConfigChatbotDto {
  enabled: boolean;
  name: string;
  avatar: string;
  greeting: string;
  limit_available_time: boolean;
  display_chatbot_sources: boolean;
  voice_response?: boolean;
  active_days: number[];
  time_from?: string;
  time_to?: string;
  conditions: SegmentsApi.SegmentValue3 | null;
}

export interface MessengerConfigDto {
  primary_color: string;
  secondary_color: string;
  logo: string;
  name: string;
  icebreakers: IcebreakerDto[];
  conversationStarters: ConversationStarterDto[];
  conditions: SegmentsApi.BrowserSegmentValueFilter | null;
  screen_position: MessengerScreenPosition;
  student_assistants: StudentAssistantsApi.StudentAssistant[];
  student_assistants_groups: StudentAssistantsApi.StudentAssistantsGroup[];
  feature_flags: Record<string, boolean>;
  background_image: string | null;
  enabled: boolean;
  greeting: string;
  intro: string;
  reply_time: MessengerReplyTime | '';
  limit_available_time: boolean;
  active_days: number[];
  time_from: string;
  time_to: string;
  timezone: string;
  chatbot?: MessengerConfigChatbotDto;
  can_expand_widget?: boolean;
  audio_enabled?: boolean;
  type?: MessengerLocationType;
}

export interface MessengerConfigCustomization {
  enabled?: boolean;
  logo?: string;
  background_image?: string | null;
  name?: string;
  primary_color?: string;
  secondary_color?: string;
  greeting?: string;
  namespace?: string;
  chatbot_name?: string;
  chatbot_logo?: string;
  starter_messages?: string[];
  forceOpened?: boolean;
  forceExpanded?: boolean;
  preview?: MessengerConfigPreview;
  hideHeaderButtons?: boolean;
  contentFit?: 'contain' | 'fill';
  type?: MessengerLocationType;
}

export interface MessengerConfigPreview {
  assistant_guid?: string;
}

export interface AuthResponse {
  user: UserDto | TokenUser;
  token: string;
}

export interface SocketQuery {
  token: string;
}

export interface AssociateUserDto {
  anonymous_guid: string;
  user_id: string;
}

export interface CreateAndAssociateUserDto {
  conversationGuid: string;
  user: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
}

export interface UserAssociatedInternalDto {
  conversation_guids: string[];
  association_data: AssociateUserDto;
  user: UserDto;
  is_online: boolean;
}

export interface UserAssociatedExternalDto {
  token: string;
  user: UserDto;
}

export enum ConversationsSettingsType {
  Rule = 'conversation_rule',
  Icebreaker = 'conversation_icebreaker',
  Settings = 'conversation_settings',
  Article = 'conversation_article',
  ConversationStarter = 'conversation_starter',
  StudentAssistant = 'conversation_student_assistant',
  StudentAssistantsGroup = 'conversation_student_assistants_group',
  ConversationView = 'conversation_view',
  ConversationArticleFolder = 'conversation_article_folder'
}

// ICEBREAKERS
export enum IcebreakerRepeatRate {
  EveryDay = '1d',
  EverySecondDay = '2d',
  EveryWeek = '1w',
  EveryMonth = '1m'
}

export interface IcebreakerDto {
  guid: string;
  variant: string;
  title: string;
  body_text: string;
  description: string;
  repeat_rate: IcebreakerRepeatRate | null;
  conditions: SegmentsApi.BrowserSegmentValueFilter | null;
  enabled: boolean;
  subdom: string;
  index_weight: number;
  updated_at: string;
  created_at: string;
  starter_message?: string;
}

export type CreateIcebreakerDto = Pick<
  IcebreakerDto,
  | 'variant'
  | 'title'
  | 'body_text'
  | 'description'
  | 'conditions'
  | 'repeat_rate'
  | 'starter_message'
>;

export type UpdateIcebreakerDto = Partial<
  Pick<
    IcebreakerDto,
    | 'variant'
    | 'title'
    | 'body_text'
    | 'description'
    | 'conditions'
    | 'enabled'
    | 'index_weight'
    | 'repeat_rate'
    | 'starter_message'
  >
>;

export interface BoltBotPreviewDto {
  uuid: string;
  name: string;
  primary_color?: string;
  secondary_color?: string;
  logo?: string;
  greeting?: string;
  urls?: string[];
  starter_messages?: string[];
  chatbot_name?: string;
  chatbot_logo?: string;
  updated_at: string;
  created_at: string;
}

export type CreateBoltBotPreviewDto = Pick<
  BoltBotPreviewDto,
  | 'name'
  | 'primary_color'
  | 'secondary_color'
  | 'logo'
  | 'greeting'
  | 'urls'
  | 'chatbot_logo'
  | 'chatbot_name'
  | 'starter_messages'
>;

export interface ConversationStarterDto {
  guid: string;
  title: string;
  message: string;
  conditions: SegmentsApi.BrowserSegmentValueFilter | null;
  enabled: boolean;
  subdom: string;
  assistants: string[] | null;
  index_weight: number;
  updated_at: string;
  created_at: string;
}

export type CreateConversationStarterDto = Pick<
  ConversationStarterDto,
  'title' | 'message' | 'conditions' | 'assistants'
>;

export type UpdateConversationStarterDto = Partial<
  Pick<
    ConversationStarterDto,
    | 'title'
    | 'message'
    | 'conditions'
    | 'enabled'
    | 'index_weight'
    | 'assistants'
  >
>;

export interface TotalArticlesInputDto {
  search?: string;
  isPrivate?: boolean;
  types?: ArticleType[];
  include?: string[];
  exclude?: string[];
}

export interface ListArticlesInputDto extends TotalArticlesInputDto {
  limit?: number;
  offset?: number;
  embedSitemap?: boolean;
  categories?: string[];
  states?: string[];
  sort?: string;
  folder?: string | null;
}

export enum ArticleType {
  Text = 'text',
  URL = 'url',
  Website = 'website',
  RssFeed = 'rss_feed',
  File = 'file',
  CustomAnswers = 'custom_answers',
  YoutubeChannel = 'youtube_channel',
  YoutubeVideo = 'youtube_video'
}

export const PREVIEW_ALLOWED_ARTICLE_TYPES = new Set([
  ArticleType.URL,
  ArticleType.File,
  ArticleType.YoutubeVideo
]);

export interface ArticleCategory {
  label: string;
  slug: string;
}

export const ARTICLE_CATEGORIES: ArticleCategory[] = [
  { label: 'Financial Aid', slug: KnowledgeBaseCategories.FinancialAid },
  { label: 'Admissions', slug: KnowledgeBaseCategories.Admissions },
  { label: 'Academics', slug: KnowledgeBaseCategories.Academics },
  { label: 'Student Life', slug: KnowledgeBaseCategories.StudentLife },
  { label: 'Athletics', slug: KnowledgeBaseCategories.Athletics },
  { label: 'Career', slug: KnowledgeBaseCategories.Career },
  { label: 'Student Services', slug: KnowledgeBaseCategories.StudentServices },
  { label: 'General', slug: KnowledgeBaseCategories.General },
  { label: 'Alumni', slug: KnowledgeBaseCategories.Alumni },
  {
    label: 'International Students',
    slug: KnowledgeBaseCategories.InternationStudents
  },
  {
    label: 'Domestic Students',
    slug: KnowledgeBaseCategories.DomesticStudents
  },
  {
    label: 'Graduate Students',
    slug: KnowledgeBaseCategories.GraduateStudents
  },
  {
    label: 'Undergraduate Students',
    slug: KnowledgeBaseCategories.UndergraduateStudents
  },
  {
    label: 'Continuing Education',
    slug: KnowledgeBaseCategories.ContinuingEducation
  },
  { label: 'Housing', slug: KnowledgeBaseCategories.Housing },
  { label: 'Technical Issues', slug: KnowledgeBaseCategories.TechnicalIssues },
  { label: 'Business Office', slug: KnowledgeBaseCategories.BusinessOffice },
  {
    label: 'Student Health & Wellness',
    slug: KnowledgeBaseCategories.StudentHealthAndWellness
  },
  {
    label: 'Campus Safety & Security',
    slug: KnowledgeBaseCategories.CampusSafetyAndSecurity
  },
  {
    label: 'Library & Academic Resources',
    slug: KnowledgeBaseCategories.LibraryAndAcademicResources
  },
  {
    label: 'Dining & Food Services',
    slug: KnowledgeBaseCategories.DiningAndFoodServices
  },
  {
    label: 'Parking & Transportation',
    slug: KnowledgeBaseCategories.ParkingAndTransportation
  },
  {
    label: 'Transfer',
    slug: KnowledgeBaseCategories.Transfer
  },
  {
    label: 'Online',
    slug: KnowledgeBaseCategories.Online
  },
  {
    label: 'Dual Credit',
    slug: KnowledgeBaseCategories.DualCredit
  },
  {
    label: 'High School',
    slug: KnowledgeBaseCategories.HighSchool
  }
];

export const ARTICLE_CATEGORIES_TAGS: TagsApi.Tag[] = ARTICLE_CATEGORIES.map(
  category => ({
    guid: category.slug,
    name: category.label,
    label: category.label
  })
);

export interface CustomAnswer {
  question: string;
  answer: string;
}

export interface SearchDocumentBlocksInputDto {
  search: string;
  limit: number;
}

export enum ArticleState {
  Indexed = 'indexed',
  Processing = 'processing',
  DailySyncProcessing = 'daily_sync_processing',
  Failed = 'failed'
}

export interface BaseArticleDto {
  guid: string;
  title: string;
  article_type: ArticleType;
  last_indexed_at: string | null;
  categories: string[];
  created_at: string | null;
  updated_at: string | null;
  state: ArticleState;
  private?: boolean;
  namespace?: string;
  folder?: string | null;
}

export interface TextArticleDto extends BaseArticleDto {
  content: string;
  article_type: ArticleType.Text;
}

export interface ArticleFromFileDto extends BaseArticleDto {
  file_guid: string;
  file?: FilesApi.ApiFile | null;
  article_type: ArticleType.File;
}

export interface UrlArticleDto extends BaseArticleDto {
  url: string;
  article_type: ArticleType.URL;
  sync_daily: boolean;

  sitemap_guid?: string;
  sitemap?: WebsiteArticleDto | RssFeedArticleDto;

  rss_feed_guid?: string;

  published_at?: string | null;
}

export interface WebsiteArticleDto extends BaseArticleDto {
  url: string;
  article_type: ArticleType.Website;
  sync_daily: boolean;
  urls: string[];
}

export interface RssFeedArticleDto extends BaseArticleDto {
  url: string;
  article_type: ArticleType.RssFeed;
  sync_daily: boolean;
  urls: string[];
}

export interface CustomAnswersArticleDto extends BaseArticleDto {
  article_type: ArticleType.CustomAnswers;
  content: string;
}

export interface YoutubeChannelArticleDto extends BaseArticleDto {
  article_type: ArticleType.YoutubeChannel;
  handle: string;
  channel_id: string;
  videos: YoutubeVideo[];
}

export interface YoutubeVideoArticleDto extends BaseArticleDto {
  url: string;
  article_type: ArticleType.YoutubeVideo;
  channel?: YoutubeChannelArticleDto;
  parent_guid?: string;
}

export type ArticleDto =
  | TextArticleDto
  | UrlArticleDto
  | ArticleFromFileDto
  | WebsiteArticleDto
  | CustomAnswersArticleDto
  | YoutubeChannelArticleDto
  | YoutubeVideoArticleDto
  | RssFeedArticleDto;

export type CreateTextArticleDto = Pick<
  TextArticleDto,
  'title' | 'categories' | 'content' | 'article_type' | 'private' | 'namespace'
>;

export interface CreateArticleFromFileDto
  extends Pick<
    ArticleFromFileDto,
    'title' | 'categories' | 'article_type' | 'private' | 'namespace' | 'folder'
  > {
  file_guid: string;
}

export type CreateCustomAnswersArticleDto = Pick<
  CustomAnswersArticleDto,
  | 'title'
  | 'categories'
  | 'article_type'
  | 'content'
  | 'private'
  | 'namespace'
  | 'folder'
>;

export type CreateUrlArticleDto = Pick<
  UrlArticleDto,
  | 'title'
  | 'categories'
  | 'url'
  | 'article_type'
  | 'sync_daily'
  | 'sitemap_guid'
  | 'private'
  | 'namespace'
  | 'folder'
>;

export type CreateWebsiteArticleDto = Pick<
  WebsiteArticleDto,
  | 'title'
  | 'categories'
  | 'url'
  | 'article_type'
  | 'sync_daily'
  | 'urls'
  | 'private'
  | 'namespace'
  | 'folder'
>;

export type CreateRssFeedArticleDto = Pick<
  RssFeedArticleDto,
  | 'title'
  | 'categories'
  | 'url'
  | 'urls'
  | 'article_type'
  | 'sync_daily'
  | 'private'
  | 'namespace'
  | 'folder'
>;

export type CreateYoutubeChannelArticleDto = Pick<
  YoutubeChannelArticleDto,
  | 'title'
  | 'categories'
  | 'article_type'
  | 'handle'
  | 'private'
  | 'namespace'
  | 'videos'
  | 'folder'
>;

export type CreateYoutubeVideoArticleDto = Pick<
  YoutubeVideoArticleDto,
  | 'title'
  | 'categories'
  | 'url'
  | 'article_type'
  | 'private'
  | 'namespace'
  | 'parent_guid'
  | 'folder'
>;

export type CreateArticleDto =
  | CreateTextArticleDto
  | CreateUrlArticleDto
  | CreateWebsiteArticleDto
  | CreateArticleFromFileDto
  | CreateCustomAnswersArticleDto
  | CreateYoutubeChannelArticleDto
  | CreateYoutubeVideoArticleDto
  | CreateRssFeedArticleDto;

export type UpdateArticleDto =
  | Partial<Pick<TextArticleDto, 'title' | 'categories' | 'content' | 'folder'>>
  | Partial<
      Pick<UrlArticleDto, 'title' | 'categories' | 'sync_daily' | 'folder'>
    >
  | Partial<Pick<CreateArticleFromFileDto, 'title' | 'categories'>>
  | Partial<Pick<WebsiteArticleDto, 'title' | 'sync_daily' | 'folder'>>
  | Partial<
      Pick<
        CustomAnswersArticleDto,
        'title' | 'categories' | 'content' | 'folder'
      >
    >
  | Partial<Pick<YoutubeChannelArticleDto, 'title' | 'categories' | 'folder'>>
  | Partial<Pick<YoutubeVideoArticleDto, 'title' | 'categories' | 'folder'>>
  | Partial<Pick<RssFeedArticleDto, 'title' | 'categories' | 'folder'>>;

// SETTINGS
export enum MessengerScreenPosition {
  Left = 'left',
  Right = 'right'
}

export enum MessengerReplyTime {
  OneMinute = '1m',
  OneHour = '1h',
  OneDay = '1d',
  OneBusinessDay = '1bd',
  FewDays = 'few_days',
  FewBusinessDays = 'few_business_days'
}

export interface ConversationsWebSettingsDto {
  enabled: boolean;
  conditions: SegmentsApi.BrowserSegmentValueFilter | null;
  avatar: string | null;
  background_image: string | null;
  screen_position: MessengerScreenPosition;
  greeting: string;
  intro: string;
  reply_time: MessengerReplyTime | '';
  limit_available_time: boolean;
  active_days: number[];
  time_from: string;
  time_to: string;
  primary_color?: string;
}

export interface ConversationsEmailSettingsDto {
  allow_anonymous_inbounds: boolean;
}

export interface ConversationsSmsSettingsDto {
  allow_anonymous_inbounds: boolean;
}

export interface ConversationsWhatsappSettingsDto {
  allow_anonymous_inbounds: boolean;
}

export interface ConversationsChatbotSettingsDto {
  chatbot_name: string | null;
  chatbot_type?: ChatbotType;
  chatbot_avatar: string | null;
  chatbot_greeting: string | null;
  chatbot_voice?: string | null;
  chatbot_voice_style?: string | null;
  conditions: SegmentsApi.SegmentValue3 | null;
  limit_available_time: boolean;
  active_days: number[];
  time_from: string;
  time_to: string;
  channels: ChatbotChannels[];
  tools: ChatbotTools[];
  display_chatbot_sources: boolean;
  voice_response?: boolean;
  limit_to_segments_email?: string[];
  limit_to_segments_sms?: string[];
  limit_to_segments_whatsapp?: string[];
  email?: {
    allow_anonymous_inbounds: boolean;
  };
  sms?: {
    allow_anonymous_inbounds: boolean;
  };
  whatsapp?: {
    allow_anonymous_inbounds: boolean;
  };
}

export interface ConversationsPhoneCallSettingsDto {
  recording_enabled: boolean;
  recording_default: boolean;
  default_phone: string | null;
}

export enum ConversationSettingsModerationFlagsActions {
  TurnOffAssistant = 'turn-off-assistant',
  BlockConversation = 'block-conversation'
}

export const MODERATION_FLAGS_ACTIONS_LABELS: Record<
  ConversationSettingsModerationFlagsActions,
  string
> = {
  [ConversationSettingsModerationFlagsActions.TurnOffAssistant]:
    'Turn off Bolt Agent',
  [ConversationSettingsModerationFlagsActions.BlockConversation]:
    'Block Conversation'
};

export enum ConversationSettingsModerationFlagsPriority {
  Alert = 'alert',
  Warning = 'warning'
}

export const MODERATION_FLAGS_PRIORITY_LABELS: Record<
  ConversationSettingsModerationFlagsPriority,
  string
> = {
  [ConversationSettingsModerationFlagsPriority.Alert]: 'Alert',
  [ConversationSettingsModerationFlagsPriority.Warning]: 'Warning'
};

export interface ConversationSettingsModerationFlagsItem {
  id: ModerationCategoriesEnum;
  actions: ConversationSettingsModerationFlagsActions[];
  priority: ConversationSettingsModerationFlagsPriority;
  message: string;
}

export interface ConversationsGeneralSettingsDto {
  sentiment_analysis_enabled?: boolean;
  transcribe_audio_messages?: boolean;
  moderation_enabled?: boolean;
  moderation_flags?: ConversationSettingsModerationFlagsItem[];
}

export interface ConversationsSettingsDto {
  web: ConversationsWebSettingsDto;
  phone_call: ConversationsPhoneCallSettingsDto;
  general: ConversationsGeneralSettingsDto;
  chatbot: ConversationsChatbotSettingsDto;
  email: ConversationsEmailSettingsDto;
  sms: ConversationsSmsSettingsDto;
  whatsapp: ConversationsWhatsappSettingsDto;
}

export interface UpdateConversationsSettingsDto {
  web?: Partial<ConversationsWebSettingsDto>;
  phone_call?: Partial<ConversationsPhoneCallSettingsDto>;
  general?: Partial<ConversationsGeneralSettingsDto>;
  chatbot?: Partial<ConversationsChatbotSettingsDto>;
  email?: Partial<ConversationsEmailSettingsDto>;
  sms?: Partial<ConversationsSmsSettingsDto>;
  whatsapp?: Partial<ConversationsWhatsappSettingsDto>;
}

export interface UpdateExternalLastVisitedDto {
  conversation_guid: string;
}

export interface TranscriptionData {
  srt: string;
}

export interface TranscriptionMedia {
  mediaId: string;
  transcripts: TranscriptionData;
}

export interface Transcription {
  media: TranscriptionMedia;
}

export interface TranscriptionSrtItem {
  id: number;
  startSeconds: number;
  endSeconds: number;
  text: string;
  speaker: string;
}

export const phoneCallLocalSettingsStorageKey = 'element451_phonecall_settings';

export interface PhoneCallLocalSettings {
  default_phone: string;
}

export enum ConversationPartSentiment {
  Negative = 1,
  Neutral = 2,
  Positive = 3
}

export interface SummarizeConversationResponse {
  summary: string;
}

export interface DraftReplyResponse {
  answer: string;
}

export interface UrlArticleIndexedDto {
  article: UrlArticleDto;
  success: boolean;
}

export interface ArticleFromFileIndexedDto {
  article: ArticleFromFileDto;
  success: boolean;
}

export interface YoutubeVideoArticleIndexedDto {
  article: YoutubeVideoArticleDto;
  success: boolean;
  error_reason?: string;
}

export interface WebsiteArticleSitemapLoadedDto {
  articles: UrlArticleDto[];
}

export enum ChatbotState {
  Running = 'running',
  StoppedByUser = 'stopped_by_user',
  StoppedByComment = 'stopped_by_comment',
  StoppedByModerationFlag = 'stopped_by_moderation_flag',
  StoppedBySystem = 'stopped_by_system',
  HandoffByChatbot = 'handoff_by_chatbot',
  RunningVoice = 'running_voice'
}

export interface ChatbotResponseSource {
  url?: string;
  title?: string;
  type?: string;
  subdom: string;
}

export type ChatbotSource = Pick<ChatbotResponseSource, 'title' | 'url'>;

export enum ChatbotResponseMetadataType {
  EventSignupTool = 'event_signup_tool',
  EventAvailabilityTool = 'event_availability_tool',
  ApplicationStartTool = 'application_start_tool',
  ScheduleAppointmentTool = 'schedule_appointment_tool',
  ScheduleAppointmentAvailabilityTool = 'schedule_appointment_availability_tool',
  ScheduleAppointmentSpecificSlotTool = 'schedule_appointment_specific_slot_tool',
  ApplicationStatusTool = 'application_status_tool',
  Link = 'link'
}

export type ChatbotResponseMetadataData =
  | string
  | EventSignupMetadata[]
  | EventAvailabilityMetadata[]
  | ApplicationsMetadata[]
  | ScheduleAppointmentMetadata[]
  | ScheduleAppointmentAvailabilityMetadata[]
  | ApplicationStatusMetadata[]
  | any;

export interface ChatbotResponseMetadata {
  type: ChatbotResponseMetadataType;
  data: ChatbotResponseMetadataData;
}

export interface ChatbotMetadataStack {
  stack: ChatbotResponseMetadata[];
}

export interface EventSignupMetadata {
  name: string;
  time: string;
  guid: string;
  tstamp: number;
}

export interface ScheduleAppointmentAvailabilityMetadata {
  display: string;
  availability: string;
  id: string;
}

export interface ScheduleAppointmentMetadata {
  name: string;
  category: string;
  guid: string;
  description: string;
}

export interface ApplicationStatusMetadata {
  submitted_at: Date;
  major: string;
  term: string;
  status: string;
  registration_id: string;
  checklist_items: string[];
  stage: string;
  application_name: string;
}

export interface EventAvailabilityMetadata {
  tstamp: number;
  time: string;
}

export interface ApplicationsMetadata {
  name: string;
  guid: string;
}

export enum EventSignupActionType {
  Signup = 'signup',
  Availability = 'availability'
}

export const CHATBOT_GLOBAL_KNOWLEDGE_BASE = 'global';

export enum ChatbotType {
  Advanced = 'advanced',
  Standard = 'standard'
}

export enum ConversationsQuickAction {
  Delete = 'delete',
  Close = 'close',
  AddToFavorites = 'add_to_favorites',
  RemoveFromFavorites = 'remove_from_favorites'
}

export enum FunctionsChatbotType {
  Chatbot = 'chatbot',
  DraftReplyWithoutPrompt = 'draft_reply_without_prompt',
  DraftReplyWithPrompt = 'draft_reply_with_prompt',
  ComposeMessage = 'compose_message'
}

export interface MessengerSendConfirmationEmailDto {
  email: string;
  client_name: string;
}

export interface MessengerConfirmVerificationCodeDto {
  email: string;
  code: string;
}

export enum MessengerConfirmVerificationCodeState {
  EmailVerifiedAndUserFound = 'email_verified_and_user_exists',
  EmailVerifiedAndUserNotFound = 'email_verified_and_user_not_found',
  EmailNotVerified = 'email_not_verified'
}

export interface MessengerConfirmVerificationCodeResponseDto {
  user?: UserDto;
  state: MessengerConfirmVerificationCodeState;
  email: string;
}

export interface BaseInboundChannel {
  type: DeliveryMethod;
  /**
   * subdom
   */
  client: string;
  /**
   * External user id
   */
  user: string;
  updated_at: Date;
  created_at: Date;
}

export interface SmsInboundChannel extends BaseInboundChannel {
  type: DeliveryMethod.Sms;
  contact: {
    /**
     * international format = +{country_code}{number}
     */
    client_phone: string;
    /**
     * international format = +{country_code}{number}
     */
    user_phone: string;
  };
  context: {
    /**
     * conversation guid
     */
    guid: string;
  };
  token: null;
}

export interface EmailInboundChannel extends BaseInboundChannel {
  type: DeliveryMethod.Email;
  /**
   * some random token TODO: find out what it represents
   */
  token: string;
}

export type InboundChannel = SmsInboundChannel | EmailInboundChannel;

export interface TwilioWhatsAppApprovedTemplate {
  language: 'en';
  date_updated: string; // '2023-10-13T06:09:04Z';
  variables: Record<string, string>; //{ '1': 'Petar' }
  friendly_name: string; // 'inquiry_start';
  account_sid: string; // 'ACe890....';
  url: string; // 'https://content.twilio.com/v1/Content/HX25c27ca4666f3f3240caaf0c741b48bf';
  sid: string; // 'HX25c27ca4666f3f3240caaf0c741b48bf';
  date_created: string; // '2023-10-13T06:09:04Z';
  types: {
    'twilio/text': {
      // used for SMS
      body: string; // 'Hi, {{1}} \nThanks for contacting Fire University. How can I help?';
    };
    // used for whatsapp
    'twilio/quick-reply': {
      body: string; // 'Hi, {{1}} \nThanks for contacting Fire University. How can I help?';
      actions: { id: string; title: string }[];
      // [
      //   { id: 'application1'; title: 'Check application status' },
      //   { id: 'apply1'; title: 'Interested in applying' },
      //   { id: 'agentid1'; title: 'Speak with an agent' }
      // ];
    };
  };
  links: {
    approval_fetch: string; // 'https://content.twilio.com/v1/Content/HX25c27ca4666f3f3240caaf0c741b48bf/ApprovalRequests';
    approval_create: string; // 'https://content.twilio.com/v1/Content/HX25c27ca4666f3f3240caaf0c741b48bf/ApprovalRequests/whatsapp';
  };
}

export interface WhatsAppApprovedTemplate {
  id: string;
  message: string;
  variables: Record<string, string>;
  name: string;
}

export const WHATSAPP_SESSION_DURATION_HOURS = 24;

interface YoutubeThumbnail {
  url: string; // 'https://i.ytimg.com/vi/7HI_9nVF2LI/default.jpg';
  width: number; // 120;
  height: number; // 90;
}

interface YoutubeThumbnails {
  default: YoutubeThumbnail;
  medium: YoutubeThumbnail;
  high: YoutubeThumbnail;
}

interface YoutubeSnippet {
  publishedAt: string; // '2024-03-12T18:48:37Z';
  channelId: string; // 'UCY7mANrofT_Z4jE8imUCc4w';
  title: string; // 'Spring Career Expo 2024';
  description: string; // 'Dreaming of landing your dream job after college? At SEMO, not only do we prep you for your career, but we also help you land it!';
  thumbnails: YoutubeThumbnails;
  channelTitle: string; // 'Southeast Missouri State University';
  liveBroadcastContent: string; // 'none';
  publishTime: string; // '2024-03-12T18:48:37Z';
}

export interface YoutubeVideo {
  kind: 'youtube#searchResult';
  etag: string; // '8TgFBTwqD9J67YD-wpi5vtA9ppg';
  id: {
    kind: 'youtube#video';
    videoId: string; // '7HI_9nVF2LI';
  };
  snippet: YoutubeSnippet;
}

export interface YoutubeChannel {
  kind: 'youtube#searchResult';
  etag: string; // 't0UetnQ26G1oVRaR0U84H5GAufE';
  id: {
    kind: 'youtube#channel';
    channelId: string; // 'UCY7mANrofT_Z4jE8imUCc4w';
  };
  snippet: YoutubeSnippet;
}

export enum ModerationCategoriesEnum {
  SEXUAL = 'sexual',
  HATE = 'hate',
  HATE_THREATENING = 'hate-threatening',
  HARASSMENT = 'harassment',
  HARASSMENT_THREATENING = 'harassment-threatening',
  SELF_HARM = 'self-harm',
  SELF_HARM_INTENT = 'self-harm-intent',
  SELF_HARM_INSTRUCTIONS = 'self-harm-instructions',
  SEXUAL_MINORS = 'sexual-minors',
  VIOLENCE = 'violence',
  VIOLENCE_GRAPHIC = 'violence-graphic',
  PROMPT_ENGINEERING = 'prompt-engineering',
  CHATBOT_BEHAVIOR_INSTRUCTIONS = 'chatbot-behavior-instructions'
}

export interface ModetarionCategories {
  [ModerationCategoriesEnum.SEXUAL]: boolean;
  [ModerationCategoriesEnum.HATE]: boolean;
  [ModerationCategoriesEnum.HARASSMENT]: boolean;
  [ModerationCategoriesEnum.SELF_HARM]: boolean;
  [ModerationCategoriesEnum.SEXUAL_MINORS]: boolean;
  [ModerationCategoriesEnum.HATE_THREATENING]: boolean;
  [ModerationCategoriesEnum.VIOLENCE_GRAPHIC]: boolean;
  [ModerationCategoriesEnum.SELF_HARM_INTENT]: boolean;
  [ModerationCategoriesEnum.SELF_HARM_INSTRUCTIONS]: boolean;
  [ModerationCategoriesEnum.HARASSMENT_THREATENING]: boolean;
  [ModerationCategoriesEnum.VIOLENCE]: boolean;
  [ModerationCategoriesEnum.PROMPT_ENGINEERING]: boolean;
  [ModerationCategoriesEnum.CHATBOT_BEHAVIOR_INSTRUCTIONS]: boolean;
}

export const MODERATION_CATEGORIES_LABELS: Record<
  ModerationCategoriesEnum,
  string
> = {
  [ModerationCategoriesEnum.SELF_HARM]: 'Self Harm',
  [ModerationCategoriesEnum.SELF_HARM_INTENT]: 'Self Harm Intent',
  [ModerationCategoriesEnum.SELF_HARM_INSTRUCTIONS]: 'Self Harm Instructions',
  [ModerationCategoriesEnum.SEXUAL]: 'Sexual',
  [ModerationCategoriesEnum.HATE]: 'Hate',
  [ModerationCategoriesEnum.HARASSMENT]: 'Harassment',
  [ModerationCategoriesEnum.SEXUAL_MINORS]: 'Sexual Minors',
  [ModerationCategoriesEnum.HATE_THREATENING]: 'Hate Threatening',
  [ModerationCategoriesEnum.VIOLENCE_GRAPHIC]: 'Violence Graphic',
  [ModerationCategoriesEnum.HARASSMENT_THREATENING]: 'Harassment Threatening',
  [ModerationCategoriesEnum.VIOLENCE]: 'Violence',
  [ModerationCategoriesEnum.PROMPT_ENGINEERING]: 'Prompt Engineering',
  [ModerationCategoriesEnum.CHATBOT_BEHAVIOR_INSTRUCTIONS]:
    'Chatbot Behavior Instructions'
};

export function convertToSeconds(hhmmss: string) {
  const time = hhmmss.split(':');

  return (
    parseInt(time[0], 10) * 3600 +
    parseInt(time[1], 10) * 60 +
    parseInt(time[2], 10)
  );
}

export function convertToMinutesSecondsString(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return (
    (mins < 10 ? '0' : '') +
    mins.toString() +
    ':' +
    (secs < 10 ? '0' : '') +
    secs.toString()
  );
}

export function createStringArrayFromSrt(srt: string) {
  const data = srt.replace(/\r/g, '');
  const regex =
    /(\d+)\n(\d{1,2}:\d{2}:\d{2},\d{1,3}) --> (\d{1,2}:\d{2}:\d{2},\d{1,3})/g;
  const data_array = data.split(regex);
  data_array.shift();
  return data_array;
}

/*

Input: srt string ->

"1\n00:00:05,65 --> 00:00:10,87\nSpeaker 2: This voicemail is not monitored\nand this recording is designed to help\n\n
2\n00:00:10,90 --> 00:00:17,14\ntest the element for 50 ones in our\ncalling features to be released later in\n\n
3\n00:00:17,17 --> 00:00:21,90\n2022. Please do not leave a\nvoicemail at this number.\n\n"

Output: TranscriptionSrtItem[]

[{
    "id": 0,
    "startSeconds": 6,
    "endSeconds": 23,
    "text": "This voicemail is not monitored\nand this recording is designed to help\ntest the element 450 one's in our
              calling\nfeatures to be released later in 2022.\nPlease do not leave a voice\nmail at this number to.",
    "speaker": "Speaker 2"
}]
*/

export function parseTranscriptionSrt(srtData: string): TranscriptionSrtItem[] {
  const data = createStringArrayFromSrt(srtData);
  const items = [];
  let id = 0;
  for (let i = 0; i < data.length; i += 4) {
    const startTime = data[i + 1].trim();
    const endTime = data[i + 2].trim();
    const text = data[i + 3].trim();

    // Grouping transcription text by Speaker:
    // We have to check if text starts with 'Speaker #'
    // to know whether to attach it to the previous part (if it's the same speaker)
    // or create a new item (if it's a different one)
    const startsWithRegex = /^Speaker \d: /g;
    const checkedString = text.match(startsWithRegex);
    if (checkedString) {
      const item: TranscriptionSrtItem = {
        id: id,
        startSeconds: convertToSeconds(startTime.split(',')[0]),
        endSeconds: convertToSeconds(endTime.split(',')[0]),
        text: text.substring(checkedString[0].length),
        speaker: checkedString[0].slice(0, -2)
      };
      id = id + 1;
      items.push(item);
    } else if (items[id - 1]) {
      items[id - 1].text += `\n${text}`;
      items[id - 1].endSeconds = convertToSeconds(endTime.split(',')[0]);
    }
  }
  return items;
}

export function formatTranscription(
  data: TranscriptionSrtItem[],
  participantName: string,
  internalParticipantName: string
) {
  let transcriptionString = '';
  data.forEach(item => {
    transcriptionString +=
      (item.speaker === 'Speaker 1'
        ? internalParticipantName
        : participantName) +
      '\n' +
      convertToMinutesSecondsString(item.startSeconds) +
      '\n' +
      item.text +
      '\n\n';
  });
  return transcriptionString;
}

export enum RealtimeVoiceSqsEventType {
  PhoneCallStarted = 'realtime-voice-phone-call-started',
  PhoneCallAssistantChunk = 'realtime-voice-phone-call-assistant-chunk',
  PhoneCallUserChunk = 'realtime-voice-phone-call-user-chunk',
  PhoneCallEnded = 'realtime-voice-phone-call-ended',
  WebVoiceCallAssistantChunk = 'realtime-voice-web-call-assistant-chunk',
  WebVoiceCallUserChunk = 'realtime-voice-web-call-user-chunk',
  PhoneCallTransferred = 'realtime-voice-phone-call-transferred'
}

interface RealtimeSqsEventContext {
  conversation_guid: string;
  date: Date;
}

interface RealtimeSqsEvent<T extends RealtimeVoiceSqsEventType> {
  event_type: T;
  event_id: string;
  subdom: string;
  context: RealtimeSqsEventContext & Record<string, unknown>;
}

interface PhoneCallStartedEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.PhoneCallStarted> {
  context: {
    conversation_guid: string;
    date: Date;
    part_guid: string;
    call_sid: string;
  };
}

interface PhoneCallUserChunkEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.PhoneCallUserChunk> {
  context: {
    conversation_guid: string;
    date: Date;
    call_sid: string;
    user_id: string | null;
    message: string;
  };
}

interface PhoneCallAssistantChunkEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.PhoneCallAssistantChunk> {
  context: {
    conversation_guid: string;
    date: Date;
    call_sid: string;
    assistant_guid: string | null;
    message: string;
  };
}

export interface PhoneCallEndedEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.PhoneCallEnded> {
  context: {
    conversation_guid: string;
    date: Date;
    part_guid: string;
    call_sid: string;
    transcript: string;
    reason?: PhoneCallEndedReason;
    duration?: string;
  };
}

interface PhoneCallTransferredEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.PhoneCallTransferred> {
  context: {
    conversation_guid: string;
    date: Date;
    call_sid: string;
    transfer_number: string;
  };
}

interface WebVoiceCallUserChunkEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.WebVoiceCallUserChunk> {
  context: {
    conversation_guid: string;
    date: Date;
    message: string;
    user_id: string;
    is_anonymous: boolean;
  };
}

interface WebVoiceCallAssistantChunkEvent
  extends RealtimeSqsEvent<RealtimeVoiceSqsEventType.WebVoiceCallAssistantChunk> {
  context: {
    conversation_guid: string;
    date: Date;
    message: string;
    assistant_guid: string | null;
  };
}

export type RealtimeSqsEvents =
  | PhoneCallStartedEvent
  | PhoneCallEndedEvent
  | PhoneCallTransferredEvent
  | PhoneCallUserChunkEvent
  | PhoneCallAssistantChunkEvent
  | WebVoiceCallUserChunkEvent
  | WebVoiceCallAssistantChunkEvent;

export interface UrlArticlePageSummaryDto {
  articleGuid: string;
  title: string;
  subtitle: string;
  content: string;
  url: string;
}

export interface EmailInboxSetting {
  /**
   * The email address of the inbox
   *
   * Example:
   *
   * admissions@tu.edu
   */
  from: string;
  /**
   * The name of the inbox
   */
  from_name: string;
  /**
   * The id of the inbox
   */
  inbox_id: string;
  /**
   * The element451 internal email address of the inbox
   *
   * Example:
   *
   * demo1-b9608bb89ccefb8e285b6440b3bb071e@element451.io
   */
  address: string;
  /**
   * The default signature of the inbox
   */
  default_signature: string;
  /**
   * Whether the inbox is active
   */
  active: boolean;
  created_at: Date;
  updated_at: Date;
  /**
   * The date the inbox was confirmed
   */
  confirmed_at: Date;

  type: 'conversations-inbox';
  app_type: 'forward-inbox';
  channel: 'email';
  driver: 'forward';
}
